import { ComponentProps, ReactNode } from "react"

import { twMerge } from "tailwind-merge"

/**
 * Props for the MainHeading component.
 */
export type MainHeadingProps = {
  /** Optional CSS class name to apply to the component */
  className?: string
  /** Optional CSS class name to apply to the actions */
  actionsClassName?: string
  /** The main heading title content */
  title: ReactNode
  /** Optional action buttons or elements to display */
  actions?: ReactNode
  /** Optional description text to display below the title */
  description?: ReactNode
  /** Optional option to hide the divider between header and rest of page content. Defaults to false. */
  hideDivider?: boolean
}

/**
 * Main heading component that displays a title with optional actions and description.
 * Provides a consistent layout for page headers with title, actions, and description.
 *
 * @param props - The component props
 * @returns The rendered main heading component
 */
export function MainHeading(props: MainHeadingProps) {
  const { className, actionsClassName, actions, title, description, hideDivider = false } = props

  return (
    <MainHeadingGroup className={className} hideDivider={hideDivider}>
      <MainHeadingTitle>{title}</MainHeadingTitle>
      {actions && <MainHeadingActions className={actionsClassName}>{actions}</MainHeadingActions>}
      {description && <MainHeadingDescription>{description}</MainHeadingDescription>}
    </MainHeadingGroup>
  )
}

/**
 * Props for the MainHeadingGroup component, extending standard hgroup HTML attributes.
 */
export type MainHeadingGroupProps = ComponentProps<"hgroup"> & { hideDivider: boolean }

/**
 * Container component that wraps the main heading elements in an hgroup element.
 * Provides responsive layout and spacing for the heading group.
 *
 * @param props - The component props, including all standard hgroup attributes
 * @returns The rendered hgroup container
 */
export function MainHeadingGroup(props: MainHeadingGroupProps) {
  const { className, hideDivider, ...restProps } = props

  return (
    <div className="@container w-full">
      <hgroup
        className={twMerge(
          "mx-6 my-8 grid grid-cols-[auto] gap-x-32 gap-y-2",
          "@4xl:grid-cols-[auto_fit-content(0px)] @4xl:grid-rows-[auto_auto]",
          className
        )}
        {...restProps}
      />
      {!hideDivider && <hr className="border-gray-200" />}
    </div>
  )
}

/**
 * Props for the MainHeadingTitle component, extending standard h1 HTML attributes.
 */
export type MainHeadingTitleProps = ComponentProps<"h1">

/**
 * Title component that renders the main heading text as an h1 element.
 * Styled with the Souvenir font and appropriate text sizing.
 *
 * @param props - The component props, including all standard h1 attributes
 * @returns The rendered h1 title element
 */
export function MainHeadingTitle(props: MainHeadingTitleProps) {
  const { className, ...restProps } = props

  return <h1 className={twMerge("font-souvenir text-4xl font-medium text-gray-900", className)} {...restProps} />
}

/**
 * Props for the MainHeadingActions component, extending standard div HTML attributes.
 */
export type MainHeadingActionsProps = ComponentProps<"div">

/**
 * Actions container component for buttons or other interactive elements.
 * Positioned to the right on larger screens and provides flex layout.
 *
 * @param props - The component props, including all standard div attributes
 * @returns The rendered actions container div
 */
export function MainHeadingActions(props: MainHeadingActionsProps) {
  const { className, ...restProps } = props

  return (
    <div
      className={twMerge(
        "mt-2 flex flex-col gap-2 *:grow",
        "@md:flex-row @md:items-end @4xl:col-start-2 @4xl:row-start-1 @4xl:row-end-3 @4xl:mt-0",
        className
      )}
      {...restProps}
    />
  )
}

/**
 * Props for the MainHeadingDescription component, extending standard p HTML attributes.
 */
export type MainHeadingDescriptionProps = ComponentProps<"p">

/**
 * Description component that renders explanatory text below the main heading.
 * Styled with larger text size and muted gray color.
 *
 * @param props - The component props, including all standard p attributes
 * @returns The rendered description paragraph element
 */
export function MainHeadingDescription(props: MainHeadingDescriptionProps) {
  const { className, ...restProps } = props

  return <p className={twMerge("col-start-1 row-start-2 text-lg text-gray-500", className)} {...restProps} />
}
